import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * PaymentsApi (Hibernate Data Service direct endpoints)
 * Use for lightweight UI queries that do not require going through PaymentsService proxy.
 * Only includes endpoints that are likely useful to the front-end (listing, summary, user subscription linkage, metadata ops).
 * NOTE: For hosted checkout + subscription creation use PaymentsServiceApi.
 */
export const PaymentsApi = {
  // --- Payment Records ---
  list: async ({ status, provider, limit = 20, cursor, from, to } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (provider) params.set('provider', provider);
    if (limit) params.set('limit', limit);
    if (cursor) params.set('cursor', cursor);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  listRecent: async (limit = 10) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/recent?limit=${encodeURIComponent(limit)}`, { method: 'GET' });
  },
  summary: async ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/summary${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  monthlyRevenue: async ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/monthly-revenue${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  createPayment: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  getPayment: async (paymentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}`, { method: 'GET' }),
  getPaymentStatus: async (paymentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/status`, { method: 'GET' }),
  reconcilePayment: async (paymentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/reconcile`, { method: 'POST' }),
  patchPayment: async (paymentId, data) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  patchPaymentMetadata: async (paymentId, metadata) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/metadata`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ metadata }) }),
  putPaymentMetadata: async (paymentId, metadata) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/metadata`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(metadata) }),
  deletePaymentMetadataKey: async (paymentId, key) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/metadata/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  refundPayment: async (paymentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/refund`, { method: 'POST' }),
  cancelPayment: async (paymentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/cancel`, { method: 'POST' }),
  paymentTimeline: async (paymentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/${paymentId}/timeline`, { method: 'GET' }),
  getByProvider: async (provider, providerPaymentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/by-provider/${provider}/${providerPaymentId}`, { method: 'GET' }),
  patchByProvider: async (provider, providerPaymentId, data) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/by-provider/${provider}/${providerPaymentId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),

  // --- Payment Intents ---
  createIntent: async (data) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/intents`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  getIntent: async (intentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/intents/${intentId}`, { method: 'GET' }),
  cancelIntent: async (intentId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/intents/${intentId}/cancel`, { method: 'POST' }),
  captureIntent: async (intentId, amountToCapture) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/intents/${intentId}/capture`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ amountToCapture }) }),
  refundIntent: async (intentId, amount) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/intents/${intentId}/refund`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ amount }) }),

  // --- User / Subscription Mirror Data ---
  upsertStripeCustomer: async (userId, stripeCustomerId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/stripe-customer`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stripeCustomerId }) }),
  getStripeCustomer: async (userId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/stripe-customer`, { method: 'GET' }),
  upsertSubscription: async (userId, data) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/subscription`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }),
  // Wrap getSubscription to handle expected 404s gracefully (normal for free tier users)
  getSubscription: async (userId) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/subscription`, { method: 'GET' });
    // Mark 404s as expected so they don't clutter the console
    if (response.status === 404) {
      response._expected404 = true;
    }
    return response;
  },
  getSubscriptionStatus: async (userId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/subscription/status`, { method: 'GET' }),
  reverseLookupByStripeCustomer: async (stripeCustomerId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/by-stripe-customer/${stripeCustomerId}`, { method: 'GET' }),
  
  /**
   * Delete all payment data for a user (admin only - for testing purposes)
   * WARNING: This permanently deletes all payment records, subscriptions, and Stripe customer data
   * @param {string|number} userId - The user ID
   * @returns {Promise<Response>}
   */
  deletePaymentData: async (userId) => fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/payment-data`, { method: 'DELETE' })
  ,
  /**
   * Additional convenience endpoints (if exposed by backend) for admin tooling / dashboards.
   * listByUser: payments linked to a specific internal userId
   * listByStripeCustomer: payments linked to a Stripe customer id stored in mirror table
   * search: free-form search (id, provider payment id, metadata) – backend must support query parameter 'q'
   */
  listByUser: async (userId, { limit = 50, cursor } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (cursor) params.set('cursor', cursor);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/by-user/${encodeURIComponent(userId)}${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  listByStripeCustomer: async (stripeCustomerId, { limit = 50, cursor } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (cursor) params.set('cursor', cursor);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/by-stripe-customer/${encodeURIComponent(stripeCustomerId)}${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  search: async (query, { limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (limit) params.set('limit', limit);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payments/search${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },

  // --- Webhook event metrics ---
  getPaymentEventSummary: async ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payment-events/metrics/summary${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  getPaymentEventTypeDistribution: async ({ from, to } = {}) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/payment-events/metrics/event-type-distribution${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }
};

export default PaymentsApi;
